// Production area shell (office, Module 04 §4 slice 04-A). Renders the
// sub-navigation and an <Outlet/> for S1/S2/S4. Registered at /production; the
// index redirects to /production/work-orders — the list is the only screen that
// answers a question without an id in hand.
//
// WHAT IS DELIBERATELY ABSENT (D-033 / spec 04 §4 slice ownership): S11/S12 —
// no link, because neither screen exists. A link to a screen with no data
// behind it is a disabled shell. S7/S8 arrived with 04-B; S6 (calibration) with
// 04-C-2, behind `production.telemetry.read` — the e20/e21 fence itself.
//
// S5 (/production/shifts) IS LINKED AS OF 04-C-5, and the two-slice gap between
// the route existing and a way in existing is the defect this line closes. The
// route landed with 04-C-4 and every test since reached it by `page.goto` — so
// nothing ever clicked a way in, and a supervisor could not find the screen at
// all. 04-C-5 then put the module's most consequential WRITE on it (the
// disposition that moves a quantity onto another shift's closed scoreboard),
// which turned "unreachable board" into "unreachable decision". This repo has
// shipped an API-only action three times on the S2 screen; the rule that came
// out of it is that a capability without a control a user can click is not
// shipped, and a screen without a link is the same defect one level up.
//
// Nav gating mirrors §7 (fail-closed — see hooks/permissions.ts): the dispatch
// board's audience is planner / facility_admin / group_admin (§4 S4), which is
// EXACTLY the holder set of `production.wo.write` — the drag affordance's own
// gate — so the link rides that code rather than a hand-kept role list.
// 04-B adds S7 behind `production.conflict.resolve` (with the §4 S7 badge
// count — an honest one-page probe, see useOpenConflictBadge) and S8 behind
// `production.floor.manage`. S5 rides `production.scoreboard.read`, the code
// the screen itself is gated on (D-087 ruling 1's role set), NOT the narrower
// `production.approve_shift_handoff`: the board is a read every one of those
// roles is entitled to, and only the hand-off panel inside it is not.
// Everything else rides `production.read`, which the route to this area is
// already gated on in routes/root.tsx.

// UX-3: the sub-navigation is the §3.10 `RouterTabs` primitive — URL-backed
// links with `aria-current`, underlined in the accent, wrapping (never
// scrolling) at ≤768 px. It used to be six bare hyperlinks borrowing the
// CATALOGUE's `.catalogue-subnav-link` class, which is both the ad-hoc-tab
// markup gate check 3a ratchets away and a cross-area style dependency: one
// module's nav rendered by another module's stylesheet.
//
// RENDER-ONCE (§5), AND THE WHOLE AREA AT ONCE. Four of the six entries are
// permission-decided, so the strip's WIDTH — and at 412 px its HEIGHT, since
// it wraps — is decided by a feed that arrives after first paint. Painting the
// strip before the mirror settles drops the page contents by a wrapped row
// when the last entry resolves. That is the exact shift UX-2 measured on the
// catalogue (CLS 0.0476) and fixed the same way: while the mirror is
// outstanding the area is a reserving skeleton, and when it settles the whole
// subtree REMOUNTS (`key` swap) so nothing painted early ever moves. Nothing
// here waits on DATA — only on the one cached role query, warm on every later
// navigation.

import { Outlet } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CardSkeleton } from "../../../components/states";
import { RouterTabs, type RouterTabDef } from "../../../components/tabs";
import { useTerm } from "../../onboarding/hooks/terminology";
import { useProductionPermissions } from "./hooks/permissions";
import { CONFLICT_BADGE_PAGE, useOpenConflictBadge } from "./hooks/production-queries";
import "./production.css";

export function ProductionLayout() {
  const { t } = useTranslation();
  const { term } = useTerm();
  const perms = useProductionPermissions();
  const canResolve = perms.has("production.conflict.resolve");
  const badge = useOpenConflictBadge(canResolve);
  const openCount = badge.data?.items.length ?? 0;
  const overflow = badge.data?.next_cursor != null;

  const tabs: RouterTabDef[] = [
    { to: "/production/work-orders", label: term("work_order") },
    ...(perms.has("production.wo.write")
      ? [{ to: "/production/dispatch", label: t("production.nav.dispatch") }]
      : []),
    ...(canResolve
      ? [
          {
            to: "/production/conflicts",
            label: (
              <>
                {t("production.nav.conflicts")}
                {/* §4 S7's badge count, on the nav entry it belongs to. It is
                    an honest one-page probe (useOpenConflictBadge), so an
                    overflowing queue reads "50+" rather than a wrong number. */}
                {openCount > 0 ? (
                  <span className="conflict-badge" data-testid="conflict-nav-badge">
                    {overflow ? `${CONFLICT_BADGE_PAGE}+` : openCount}
                  </span>
                ) : null}
              </>
            ),
          },
        ]
      : []),
    ...(perms.has("production.floor.manage")
      ? [{ to: "/production/devices", label: t("production.nav.devices") }]
      : []),
    // 04-C-5 — S5, the shift scoreboard and its defect hand-off ledger.
    ...(perms.has("production.scoreboard.read")
      ? [{ to: "/production/shifts", label: t("production.nav.shifts") }]
      : []),
    // 04-C-2 — S6 rides the e20/e21 fence (§7's telemetry role set).
    ...(perms.has("production.telemetry.read")
      ? [{ to: "/production/calibration", label: t("production.nav.calibration") }]
      : []),
    // S13 — the blocked-stations board, on the SAME code, because it rides the
    // same endpoint set (§5 e22). `maintenance` holds it and holds no office
    // code at all, so this is the only production entry that role can follow to
    // a machine fact — which is exactly why the board is not a section of the
    // dispatch board, whose entry rides `production.wo.write`.
    ...(perms.has("production.telemetry.read")
      ? [{ to: "/production/blocked", label: t("production.nav.blocked") }]
      : []),
    // S14 — the piece-rate ledger, on `production.piece_rate.read`: e27's own
    // fence, so the entry's audience and the screen's data agree by
    // construction. It is a DIFFERENT audience from every other entry here —
    // finance holds this code and neither `production.wo.write` nor
    // `production.telemetry.read`, so without this link the whole capture
    // ledger stays where it was for three slices: on the wire, and on no screen.
    ...(perms.has("production.piece_rate.read")
      ? [{ to: "/production/piece-rate", label: t("production.nav.pieceRate") }]
      : []),
  ];

  if (!perms.isResolved) {
    return (
      <div className="production-area" key="prod-loading" data-testid="production-loading">
        <div className="ds-tabs prod-subnav-skel" aria-hidden="true" />
        <div className="production-body">
          <CardSkeleton lines={6} label={t("production.common.loading")} />
        </div>
      </div>
    );
  }

  return (
    <div className="production-area" key="prod-ready">
      <RouterTabs tabs={tabs} label={t("production.nav.aria")} data-testid="production-subnav" />
      <div className="production-body">
        <Outlet />
      </div>
    </div>
  );
}
